<script setup>
import { ref } from 'vue';

import { storeToRefs } from 'pinia';

import Dialog from 'primevue/dialog';

import { useAppVersionStore } from '@/stores/appVersion';

import VersionUpdateNotes from './VersionUpdateNotes.vue';

const emit = defineEmits(['reload', 'dismiss']);

// Only a breaking build opens this dialog for a new version (non-breaking ones use
// VersionUpdateNotice), so the notes below are the notes of that breaking build.
const { notes, notesMode, prs } = storeToRefs(useAppVersionStore());

const visible = ref(false);
// 'version' = a genuinely newer build was detected by the poll; 'chunk' = a dynamic import
// failed with no new version (transient / stale chunk). Drives the copy so we never claim a
// new release when there isn't one.
const reason = ref('version');

const show = (nextReason = 'version') => {
  reason.value = nextReason;
  visible.value = true;
};

const hide = () => {
  visible.value = false;
};

const handleReload = () => {
  emit('reload');
  hide();
};

const handleDismiss = () => {
  emit('dismiss');
  hide();
};

defineExpose({ show, hide });
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :modal="true"
    :closable="false"
    :dismissable-mask="false"
    :draggable="false"
    class="popup set-popup sp-popup version-update-dialog"
    :style="{ width: '420px' }"
  >
    <div class="popup-header">
      <div class="popup-icon">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
            fill="currentColor"
          />
          <path
            d="M12 17L17 12L15.59 10.59L13 13.17V7H11V13.17L8.41 10.59L7 12L12 17Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <h3 class="popup-title">
        {{ reason === 'chunk' ? $t('Reload required') : $t('Update Available') }}
      </h3>
      <p class="popup-description">
        {{
          reason === 'chunk'
            ? $t('Part of the app failed to load. Reload the page to continue.')
            : $t(
              'A new version of WisePPC is available. Reload the page to get the latest features and improvements.',
            )
        }}
      </p>
    </div>

    <VersionUpdateNotes
      v-if="reason === 'version'"
      class="version-update-dialog-notes"
      :notes="notes"
      :notes-mode="notesMode"
      :prs="prs"
    />

    <div class="confirm-btns-wrap">
      <VBtn
        variant="tonal"
        class="cancel-button"
        @click="handleDismiss"
      >
        {{ $t('Later') }}
      </VBtn>
      <VBtn
        color="primary"
        @click="handleReload"
      >
        {{ $t('Reload Now') }}
      </VBtn>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
.version-update-dialog {
  :deep(.p-dialog-content) {
    padding: 24px !important;
    padding-top: 20px !important;
  }

  .popup-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 24px;
  }

  .popup-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      color-mix(in srgb, var(--wp-brand-60) 15%, transparent) 0%,
      color-mix(in srgb, var(--wp-brand-60) 5%, transparent) 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: var(--wp-text-accent);

    svg {
      transform: rotate(180deg);
    }
  }

  .popup-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--wp-text-accent);
    margin: 0 0 8px 0;
    letter-spacing: -0.01em;
  }

  .popup-description {
    font-size: 0.875rem;
    color: var(--wp-text-tertiary);
    margin: 0;
    line-height: 1.5;
    max-width: 320px;
  }

  .version-update-dialog-notes {
    margin-bottom: 20px;
  }

  .confirm-btns-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 12px;

    .cancel-button {
      min-width: 100px;
    }

    .v-btn {
      min-width: 120px;
    }
  }
}

:global(.dark) {
  .version-update-dialog {
    .popup-icon {
      background: linear-gradient(
        135deg,
        color-mix(in srgb, var(--wp-brand-60) 20%, transparent) 0%,
        color-mix(in srgb, var(--wp-brand-60) 8%, transparent) 100%
      );
    }
  }
}
</style>
